import {
  type ContractData,
  CustomerType,
  type PersonalData,
} from '@/storyblok/PageContractConclusion/PageContractConclusionTypes';

import { TargetGroup } from './enums';
import type { ReplacementsType } from './replacements';

/**
 * Returns if a contract partner is provided
 */
export function hasValidContractPartner(customerType: string | undefined, contractPartnerData: PersonalData) {
  return (
    !!contractPartnerData &&
    isGroup(customerType) &&
    !!contractPartnerData.firstname &&
    contractPartnerData.firstname.length > 0 &&
    !!contractPartnerData.lastname &&
    contractPartnerData.lastname.length > 0
  );
}

/**
 * Returns whether the customer has type 'group'
 */
export function isGroup(customerType: string | undefined) {
  return !!customerType && Number(customerType) === CustomerType.Group;
}

export function isOrganization(customerType: string | undefined) {
  return !!customerType && Number(customerType) === CustomerType.Organization;
}

export function isIndividual(customerType: string | undefined) {
  return !!customerType && Number(customerType) === CustomerType.Individual;
}

/**
 * Returns the name of the person formatted
 */
export function getPersonalDataName(customerType: string | undefined, personalData: PersonalData) {
  let subline = personalData.title && isGroup(customerType) ? `${personalData.title} ` : '';
  subline += `${personalData.firstname} ${personalData.lastname}`;
  return subline;
}

/**
 * Generates common replacements for placehoders in texts, which are based on the order as whole
 */
export function getReplacementTexts(
  user: PersonalData | undefined,
  partner: PersonalData | undefined,
  customerType: string | undefined
): ReplacementsType {
  const replacements: ReplacementsType = [];

  if (user) {
    let greeting = 'Guten Tag';

    if (!isOrganization(customerType)) {
      greeting += ` ${getPersonalDataName(customerType, user)}`;
      if (hasValidContractPartner(customerType, partner!)) {
        greeting += `, ${getPersonalDataName(customerType, partner!)}`;
      }
    }

    replacements.push({
      search: '%%name%%',
      replace: greeting,
    });
  }

  return replacements;
}

/**
 * Generates replacements for placehoders in texts, which are based on a specific contract in the order
 */
export function getContractSpecificReplacementTexts(contract: ContractData): ReplacementsType {
  const replacements: ReplacementsType = [];

  const isCommercialContract = contract.tariffProductTargetGroup === TargetGroup.GK;
  const grossOrNetText = !isCommercialContract ? '(brutto)' : '(netto)';

  replacements.push({
    search: '%%pricesDate%%',
    replace: contract.tariffPricesDate,
  });

  replacements.push({
    search: '%%grossOrNetText%%',
    replace: grossOrNetText,
  });

  return replacements;
}
